import { http } from '@/utils/http'
import { getConfig } from '@/config'
import { ApiService } from './api.service'

/**
 * Event types for tracking
 */
export enum TrackingEventType {
  Authentication = 'authentication',
  Subscription = 'subscription',
  Purchase = 'purchase',
  Search = 'search',
  Playback = 'playback',
  Engagement = 'engagement'
}

/**
 * Events that can be tracked
 */
export enum TrackingEvent {
  // Authentication events
  UserLoggedIn = 'login',
  UserLoggedOut = 'logout',
  UserRegistered = 'registration',
  
  // Subscription events
  UserSubscribed = 'subscribed',
  
  // Purchase events
  UserPurchasedTitle = 'purchased_title',
  
  // Search events
  SearchKeyword = 'search',
  
  // Playback events
  PlaybackPlay = 'play',
  PlaybackPause = 'pause',
  PlaybackStart = 'start',
  PlaybackFirstQuartile = 'first_quartile',
  PlaybackMidpoint = 'midpoint',
  PlaybackThirdQuartile = 'third_quartile',
  PlaybackComplete = 'complete',
  PlaybackProgress = 'progress'
}

/**
 * Interface for tracking data payload
 */
export interface TrackingPayload {
  app_id?: number;
  title_id?: string;
  track_id?: string;
  title?: string;
  event: TrackingEvent;
  data?: Record<string, any>;
  is_ad?: boolean;
  timestamp?: string;
  [key: string]: any;
}

/**
 * Service for tracking user events
 */
export class TrackingService {
  private apiService: ApiService;
  private readonly TRACK_ENDPOINT = '/event';
  
  constructor(apiService: ApiService) {
    this.apiService = apiService;
  }

  private bridgeAnalyticsEvent(payload: TrackingPayload): void {
    void import('@/lib/analytics').then(({ bridgeToAnalytics }) => {
      if (payload.event === TrackingEvent.PlaybackStart) {
        bridgeToAnalytics('play_content', {
          title_id: payload.title_id,
          track_id: payload.track_id,
        })
      } else if (payload.event === TrackingEvent.SearchKeyword) {
        bridgeToAnalytics('search', {
          search_term: payload.data?.keyword,
        })
      }
    }).catch(() => {
      // Analytics bridge is optional
    })
  }
  
  /**
   * Track an event
   * @param type - The type of event
   * @param payload - The event payload
   * @returns A promise that resolves when the event is tracked
   */
  async trackEvent(type: TrackingEventType, payload: TrackingPayload): Promise<void> {
    const config = getConfig();
    
    // Set default values if not provided
    const trackingPayload = {
      ...payload,
      app_id: payload.app_id || config.app.contentId,
      timestamp: payload.timestamp || new Date().toISOString()
    };
    
    try {
      await this.apiService.post(this.TRACK_ENDPOINT, {
        type,
        payload: trackingPayload
      });
      this.bridgeAnalyticsEvent(trackingPayload);
    } catch (error) {
      // Silently handle tracking errors to not disrupt user experience
      console.error('Tracking error:', error);
    }
  }
  
  private async trackAuthEvent(
    type: TrackingEvent.UserLoggedIn | TrackingEvent.UserLoggedOut | TrackingEvent.UserRegistered,
  ): Promise<void> {
    try {
      await this.apiService.post(this.TRACK_ENDPOINT, {
        type,
        payload: {
          timestamp: new Date().toISOString(),
        },
      })
    } catch (error) {
      console.error('Tracking error:', error)
    }
  }

  // Authentication events
  async trackLogin(): Promise<void> {
    return this.trackAuthEvent(TrackingEvent.UserLoggedIn)
  }
  
  async trackLogout(): Promise<void> {
    return this.trackAuthEvent(TrackingEvent.UserLoggedOut)
  }
  
  async trackRegistration(): Promise<void> {
    return this.trackAuthEvent(TrackingEvent.UserRegistered)
  }
  
  // Subscription events
  async trackSubscription(data?: Record<string, any>): Promise<void> {
    return this.trackEvent(TrackingEventType.Subscription, {
      event: TrackingEvent.UserSubscribed,
      data
    });
  }
  
  // Purchase events
  async trackPurchase(titleId: string, title: string, data?: Record<string, any>): Promise<void> {
    return this.trackEvent(TrackingEventType.Purchase, {
      event: TrackingEvent.UserPurchasedTitle,
      title_id: titleId,
      title,
      data
    });
  }
  
  // Search events
  async trackSearch(keyword: string): Promise<void> {
    return this.trackEvent(TrackingEventType.Search, {
      event: TrackingEvent.SearchKeyword,
      data: { keyword }
    });
  }
  
  // Playback events
  async trackPlaybackAction(
    titleId: string, 
    trackId: string, 
    title: string, 
    event: TrackingEvent.PlaybackPlay | TrackingEvent.PlaybackPause,
    position: number,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    return this.trackEvent(TrackingEventType.Playback, {
      event,
      title_id: titleId,
      track_id: trackId,
      title,
      is_ad: isAd,
      data: {
        position,
        duration,
        is_ad: isAd
      }
    });
  }
  
  async trackPlaybackProgress(
    titleId: string,
    trackId: string,
    title: string,
    event: TrackingEvent,
    position: number,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    return this.trackEvent(TrackingEventType.Playback, {
      event,
      title_id: titleId,
      track_id: trackId,
      title,
      is_ad: isAd,
      data: {
        position,
        duration,
        is_ad: isAd
      }
    });
  }
  
  /**
   * Track playback start
   */
  async trackPlaybackStart(
    titleId: string,
    trackId: string,
    title: string,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    return this.trackPlaybackProgress(
      titleId,
      trackId,
      title,
      TrackingEvent.PlaybackStart,
      0,
      duration,
      isAd
    );
  }
  
  /**
   * Track playback progress at first quartile
   */
  async trackPlaybackFirstQuartile(
    titleId: string,
    trackId: string,
    title: string,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    const position = Math.floor(duration * 0.25);
    return this.trackPlaybackProgress(
      titleId,
      trackId,
      title,
      TrackingEvent.PlaybackFirstQuartile,
      position,
      duration,
      isAd
    );
  }
  
  /**
   * Track playback progress at midpoint
   */
  async trackPlaybackMidpoint(
    titleId: string,
    trackId: string,
    title: string,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    const position = Math.floor(duration * 0.5);
    return this.trackPlaybackProgress(
      titleId,
      trackId,
      title,
      TrackingEvent.PlaybackMidpoint,
      position,
      duration,
      isAd
    );
  }
  
  /**
   * Track playback progress at third quartile
   */
  async trackPlaybackThirdQuartile(
    titleId: string,
    trackId: string,
    title: string,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    const position = Math.floor(duration * 0.75);
    return this.trackPlaybackProgress(
      titleId,
      trackId,
      title,
      TrackingEvent.PlaybackThirdQuartile,
      position,
      duration,
      isAd
    );
  }
  
  /**
   * Track playback completion
   */
  async trackPlaybackComplete(
    titleId: string,
    trackId: string,
    title: string,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    return this.trackPlaybackProgress(
      titleId,
      trackId,
      title,
      TrackingEvent.PlaybackComplete,
      duration,
      duration,
      isAd
    );
  }
  
  /**
   * Track regular playback progress (every 15 seconds)
   */
  async trackPlaybackIntervalProgress(
    titleId: string,
    trackId: string,
    title: string,
    position: number,
    duration: number,
    isAd: boolean = false
  ): Promise<void> {
    return this.trackPlaybackProgress(
      titleId,
      trackId,
      title,
      TrackingEvent.PlaybackProgress,
      position,
      duration,
      isAd
    );
  }
} 